<template>
    <md-dialog ref="noteslistPlans" class="noteslist-plans-modal" md-theme="default">
        <md-dialog-title>
            <div class="noteslist-plans-modal-title">Notes List plans</div>
        </md-dialog-title>
        <md-dialog-content class="noteslist-plans-modal-content">
            <div class="noteslist-plans-status-block">
                <div class="noteslist-plans-status-summary">
                    <div class="noteslist-plans-status-text">
                        <span class="noteslist-plans-summary-label">Current access</span>
                        <span class="noteslist-plans-active-plan-name">{{ currentActivePlanName }}</span>
                    </div>
                    <span
                        class="noteslist-account-badge"
                        :class="hasActiveIntegrationAccess ? 'is-linked' : 'is-disconnected'">
                        {{ hasActiveIntegrationAccess ? 'Active' : 'Not active' }}
                    </span>
                </div>
                <p class="noteslist-plans-summary-hint" v-if="periodEndLabel">
                    Access period ends: {{ periodEndLabel }}
                </p>
                <p class="noteslist-plans-summary-hint" v-if="user.active_pay && !notesListLinked">
                    Connect your Notes List account in Account Settings to unlock Cuelist + Notes List plans.
                </p>
            </div>

            <div class="noteslist-plans-section">
                <h3 class="noteslist-plans-section-title">Cuelist + Notes List (Dual)</h3>
                <p class="noteslist-plans-section-copy">
                    Full Collaborator access, Notes List partner license, and structure sync.
                </p>
                <template v-if="!user.active_pay">
                    <div class="noteslist-plans-actions-row">
                        <div class="noteslist-plans-interval-row">
                            <button
                                type="button"
                                class="noteslist-plans-chip"
                                :class="{ active: dualUpgradeInterval === 'month' }"
                                v-if="dualMonthlyPlanId"
                                :disabled="baseIntervalLock === 'year'"
                                @click="dualUpgradeInterval = 'month'">
                                Monthly
                            </button>
                            <button
                                type="button"
                                class="noteslist-plans-chip"
                                :class="{ active: dualUpgradeInterval === 'year' }"
                                v-if="dualYearlyPlanId"
                                :disabled="baseIntervalLock === 'month'"
                                @click="dualUpgradeInterval = 'year'">
                                Annual
                            </button>
                        </div>
                        <md-button
                            class="md-raised md-primary noteslist-plans-action-btn"
                            :disabled="dualUpgradeWorking || !dualUpgradeInterval"
                            @click="upgradeToDualPlan">
                            {{ dualUpgradeWorking ? 'Working…' : 'Subscribe with Dual' }}
                        </md-button>
                    </div>
                </template>
                <template v-else-if="canSwitchDualInterval">
                    <div class="noteslist-plans-actions-row">
                        <div class="noteslist-plans-interval-row">
                            <button
                                type="button"
                                class="noteslist-plans-chip"
                                :class="{ active: dualUpgradeInterval === 'month' }"
                                v-if="dualMonthlyPlanId"
                                @click="selectDualInterval('month')">
                                Monthly{{ currentDualInterval === 'month' ? ' (current)' : '' }}
                            </button>
                            <button
                                type="button"
                                class="noteslist-plans-chip"
                                :class="{ active: dualUpgradeInterval === 'year' }"
                                v-if="dualYearlyPlanId"
                                @click="selectDualInterval('year')">
                                Annual{{ currentDualInterval === 'year' ? ' (current)' : '' }}
                            </button>
                        </div>
                        <md-button
                            class="md-raised md-primary noteslist-plans-action-btn"
                            :disabled="dualUpgradeWorking || dualPreviewLoading || dualUpgradeInterval === currentDualInterval"
                            @click="confirmOrPreviewDualUpgrade">
                            {{ dualActionLabel('Switch plan') }}
                        </md-button>
                        <a
                            v-if="awaitingDualConfirm"
                            href="#"
                            class="noteslist-plans-cancel-link"
                            @click.prevent="cancelDualConfirm">
                            Cancel
                        </a>
                    </div>
                </template>
                <template v-else-if="canUpgradeToDual">
                    <div class="noteslist-plans-actions-row">
                        <div class="noteslist-plans-interval-row">
                            <button
                                type="button"
                                class="noteslist-plans-chip"
                                :class="{ active: dualUpgradeInterval === 'month' }"
                                v-if="dualMonthlyPlanId"
                                :disabled="baseIntervalLock === 'year'"
                                @click="selectDualInterval('month')">
                                Monthly
                            </button>
                            <button
                                type="button"
                                class="noteslist-plans-chip"
                                :class="{ active: dualUpgradeInterval === 'year' }"
                                v-if="dualYearlyPlanId"
                                :disabled="baseIntervalLock === 'month'"
                                @click="selectDualInterval('year')">
                                Annual
                            </button>
                        </div>
                        <md-button
                            class="md-raised md-primary noteslist-plans-action-btn"
                            :disabled="dualUpgradeWorking || dualPreviewLoading || !dualUpgradeInterval"
                            @click="confirmOrPreviewDualUpgrade">
                            {{ dualActionLabel('Upgrade to Dual') }}
                        </md-button>
                        <a
                            v-if="awaitingDualConfirm"
                            href="#"
                            class="noteslist-plans-cancel-link"
                            @click.prevent="cancelDualConfirm">
                            Cancel
                        </a>
                    </div>
                </template>
                <p class="noteslist-plans-preview" v-if="dualPreview && awaitingDualConfirm">
                    <span v-if="dualPreview.deferred">{{ dualPreview.message }}</span>
                    <span v-else>Estimated charge today: ${{ dualPreview.amount_due_display || '0.00' }}</span>
                </p>
                <p class="noteslist-plans-error" v-if="dualUpgradeError">{{ dualUpgradeError }}</p>
            </div>

            <div class="noteslist-plans-section" v-if="showDualTeamSection">
                <h3 class="noteslist-plans-section-title">Cuelist + Notes List — Educational / Institutional</h3>
                <p class="noteslist-plans-section-copy">
                    Team seats on Notes List, bundled with your Cuelist Educational or Institutional plan.
                </p>
                <div class="noteslist-plans-actions-row">
                    <div class="noteslist-plans-interval-row">
                        <button
                            type="button"
                            class="noteslist-plans-chip"
                            :class="{ active: dualTeamTarget === 'dual_educational' }"
                            v-if="canUpgradeToDualEducational"
                            @click="selectDualTeamTarget('dual_educational')">
                            Educational
                        </button>
                        <button
                            type="button"
                            class="noteslist-plans-chip"
                            :class="{ active: dualTeamTarget === 'dual_institution' }"
                            v-if="canUpgradeToDualInstitutional"
                            @click="selectDualTeamTarget('dual_institution')">
                            Institutional
                        </button>
                    </div>
                    <md-button
                        class="md-raised md-primary noteslist-plans-action-btn"
                        :disabled="dualTeamWorking || dualTeamPreviewLoading || !dualTeamTarget"
                        @click="confirmOrPreviewDualTeamUpgrade">
                        {{ dualTeamActionLabel('Upgrade') }}
                    </md-button>
                    <a
                        v-if="awaitingDualTeamConfirm"
                        href="#"
                        class="noteslist-plans-cancel-link"
                        @click.prevent="cancelDualTeamConfirm">
                        Cancel
                    </a>
                </div>
                <p class="noteslist-plans-preview" v-if="dualTeamPreview && awaitingDualTeamConfirm">
                    <span v-if="dualTeamPreview.deferred">{{ dualTeamPreview.message }}</span>
                    <span v-else>Estimated charge today: ${{ dualTeamPreview.amount_due_display || '0.00' }}</span>
                </p>
                <p class="noteslist-plans-error" v-if="dualTeamError">{{ dualTeamError }}</p>
            </div>
        </md-dialog-content>
        <md-dialog-actions>
            <md-button class="md-primary" @click="close">Close</md-button>
        </md-dialog-actions>
    </md-dialog>
</template>

<script>
import { mapGetters } from 'vuex';
import { hasNotesListIntegrationAccess } from '@/lib/noteslist-integration-access';
import { stashPendingPlanUpdate } from '@/lib/pending-plan-update';

export default {
    name: 'noteslistPlans',
    data() {
        return {
            dualMonthlyPlanId: process.env.VUE_APP_STRIPE_DUAL_MONTHLY_PLANID || '',
            dualYearlyPlanId: process.env.VUE_APP_STRIPE_DUAL_YEARLY_PLANID || '',
            dualEducationalPlanId: process.env.VUE_APP_STRIPE_DUAL_EDUCATIONAL_PLANID || '',
            dualInstitutionPlanId: process.env.VUE_APP_STRIPE_DUAL_INSTITUTION_PLANID || '',
            dualUpgradeInterval: 'month',
            dualUpgradeWorking: false,
            dualUpgradeError: '',
            dualPreview: null,
            dualPreviewLoading: false,
            awaitingDualConfirm: false,
            dualTeamTarget: '',
            dualTeamWorking: false,
            dualTeamError: '',
            dualTeamPreview: null,
            dualTeamPreviewLoading: false,
            awaitingDualTeamConfirm: false
        };
    },
    computed: {
        ...mapGetters(['user']),
        hasActiveIntegrationAccess() {
            return hasNotesListIntegrationAccess(this.user);
        },
        isOnDual() {
            return !!(
                this.user
                && this.user.noteslist_integration_access
                && this.user.noteslist_integration_source === 'dual'
            );
        },
        isOnSyncOnly() {
            return !!(
                this.user
                && this.user.noteslist_integration_access
                && this.user.noteslist_integration_source === 'sync_only'
            );
        },
        currentActivePlanName() {
            if (this.isOnDual) {
                return 'Cuelist + Notes List (Dual)';
            }
            if (this.isOnSyncOnly) {
                return 'Structure sync only';
            }
            if (this.hasActiveIntegrationAccess) {
                return 'Integration access';
            }
            return 'No Notes List plan';
        },
        periodEndLabel() {
            const periodEnd = this.user && this.user.noteslist_integration_period_end;
            if (!periodEnd) {
                return '';
            }
            const date = periodEnd.toDate ? periodEnd.toDate() : new Date(periodEnd);
            if (Number.isNaN(date.getTime())) {
                return '';
            }
            return date.toLocaleString();
        },
        currentDualInterval() {
            if (!this.isOnDual) {
                return null;
            }
            if (this.user.price_id && this.user.price_id === this.dualYearlyPlanId) {
                return 'year';
            }
            if (this.user.price_id && this.user.price_id === this.dualMonthlyPlanId) {
                return 'month';
            }
            return null;
        },
        canSwitchDualInterval() {
            // Only true Dual Individual has a Monthly/Annual choice — Dual
            // Educational/Institutional are year-only team tiers, and letting
            // them through here would let this toggle silently swap a team
            // plan down to Dual Individual pricing, dropping their seats.
            return (
                this.subscriptionFamily === 'dual_individual' &&
                !!(this.dualMonthlyPlanId && this.dualYearlyPlanId)
            );
        },
        canUpgradeToDual() {
            if (!this.user || !this.user.active_pay) {
                return false;
            }
            if (this.isOnDual) {
                return false;
            }
            const level = this.user.subscription_level || '';
            if (level === 'educational' || level === 'institutional') {
                // These upgrade to their own Dual counterpart only, below —
                // not to Dual Individual.
                return false;
            }
            if (!this.notesListLinked) {
                return false;
            }
            return !!(this.dualMonthlyPlanId || this.dualYearlyPlanId);
        },
        baseIntervalLock() {
            const level = this.user && this.user.subscription_level;
            if (level === 'student') {
                // The student rate comes in both intervals; the price says which.
                const priceId = this.user.price_id;
                if (priceId && priceId === process.env.VUE_APP_STRIPE_STUDENT_YEARLY_PLANID) return 'year';
                if (priceId && priceId === process.env.VUE_APP_STRIPE_STUDENT_MONTHLY_PLANID) return 'month';
                return null;
            }
            if (level === 'monthly') {
                return 'month';
            }
            if (level === 'yearly' || level === 'educational' || level === 'institutional') {
                return 'year';
            }
            return null;
        },
        notesListLinked() {
            return !!(this.user && this.user.noteslist_user_id);
        },
        subscriptionFamily() {
            if (!this.user) {
                return 'individual';
            }
            const level = this.user.subscription_level || '';
            if (this.isOnDual) {
                if (level === 'dual_educational') return 'dual_educational';
                if (level === 'dual_institution') return 'dual_institution';
                return 'dual_individual';
            }
            if (level === 'educational') return 'educational';
            if (level === 'institutional') return 'institutional';
            return 'individual';
        },
        // Mirrors TEAM_UPGRADE_TARGETS in switch-plan-billing.js — keep in sync.
        dualTeamTargetsAllowed() {
            const map = {
                individual: ['dual_educational', 'dual_institution'],
                dual_individual: ['dual_educational', 'dual_institution'],
                educational: ['dual_educational'],
                institutional: ['dual_institution'],
                dual_educational: [],
                dual_institution: []
            };
            return map[this.subscriptionFamily] || [];
        },
        canUpgradeToDualEducational() {
            return !!(
                this.user &&
                this.user.active_pay &&
                this.notesListLinked &&
                this.dualEducationalPlanId &&
                this.dualTeamTargetsAllowed.includes('dual_educational')
            );
        },
        canUpgradeToDualInstitutional() {
            return !!(
                this.user &&
                this.user.active_pay &&
                this.notesListLinked &&
                this.dualInstitutionPlanId &&
                this.dualTeamTargetsAllowed.includes('dual_institution')
            );
        },
        showDualTeamSection() {
            return this.canUpgradeToDualEducational || this.canUpgradeToDualInstitutional;
        }
    },
    methods: {
        open() {
            this.dualUpgradeError = '';
            this.dualUpgradeWorking = false;
            this.dualPreview = null;
            this.dualPreviewLoading = false;
            this.awaitingDualConfirm = false;
            const lock = this.baseIntervalLock;
            if (lock === 'year' && this.dualYearlyPlanId) {
                this.dualUpgradeInterval = 'year';
            } else if (lock === 'month' && this.dualMonthlyPlanId) {
                this.dualUpgradeInterval = 'month';
            } else if (this.dualMonthlyPlanId) {
                this.dualUpgradeInterval = 'month';
            } else if (this.dualYearlyPlanId) {
                this.dualUpgradeInterval = 'year';
            }
            this.$refs.noteslistPlans.open();
        },
        close() {
            this.$refs.noteslistPlans.close();
        },
        selectDualInterval(interval) {
            this.dualUpgradeInterval = interval;
            this.dualPreview = null;
            this.awaitingDualConfirm = false;
            this.dualUpgradeError = '';
        },
        cancelDualConfirm() {
            this.dualPreview = null;
            this.awaitingDualConfirm = false;
            this.dualUpgradeError = '';
        },
        dualActionLabel(baseLabel) {
            if (this.dualUpgradeWorking) return 'Working…';
            if (this.dualPreviewLoading) return 'Checking…';
            if (this.awaitingDualConfirm) {
                const amount =
                    this.dualPreview &&
                    !this.dualPreview.deferred &&
                    !this.dualPreview.same_price &&
                    this.dualPreview.amount_due_display;
                return amount ? `Confirm — charge $${amount}` : 'Confirm switch';
            }
            return baseLabel;
        },
        async confirmOrPreviewDualUpgrade() {
            if (!this.dualUpgradeInterval || this.dualUpgradeWorking) {
                return;
            }
            if (this.awaitingDualConfirm) {
                await this.upgradeToDualPlan();
                return;
            }
            this.dualPreviewLoading = true;
            this.dualUpgradeError = '';
            try {
                this.dualPreview = await this.$store.dispatch('previewNotesListSwitch', {
                    target: 'dual',
                    interval: this.dualUpgradeInterval
                });
                this.awaitingDualConfirm = true;
            } catch (err) {
                this.dualPreview = null;
                this.dualUpgradeError = (err && err.message) || 'Could not preview switch.';
            } finally {
                this.dualPreviewLoading = false;
            }
        },
        async upgradeToDualPlan() {
            if (!this.dualUpgradeInterval || this.dualUpgradeWorking) {
                return;
            }
            this.dualUpgradeWorking = true;
            this.dualUpgradeError = '';
            try {
                const result = await this.$store.dispatch('upgradeNotesListToDual', {
                    interval: this.dualUpgradeInterval
                });
                if (result.mode === 'checkout' && result.url) {
                    stashPendingPlanUpdate(this.user);
                    window.location.href = result.url;
                    return;
                }
                if (result.mode === 'swapped' || result.mode === 'already_dual') {
                    let successMessage = 'Upgraded to Cuelist + Notes List.';
                    if (result.mode === 'already_dual') {
                        successMessage = 'You already have that Cuelist + Notes List plan.';
                    } else if (this.isOnDual) {
                        successMessage = 'Switched Cuelist + Notes List billing.';
                    }
                    this.$toast.success(successMessage);
                    this.close();
                    this.$emit('plans-updated');
                    return;
                }
                this.dualUpgradeError = 'Unexpected response from billing. Please try again.';
            } catch (err) {
                this.dualUpgradeError = (err && err.message) || 'Upgrade failed. Please try again.';
            } finally {
                this.dualUpgradeWorking = false;
            }
        },
        selectDualTeamTarget(target) {
            this.dualTeamTarget = target;
            this.dualTeamPreview = null;
            this.awaitingDualTeamConfirm = false;
            this.dualTeamError = '';
        },
        cancelDualTeamConfirm() {
            this.dualTeamPreview = null;
            this.awaitingDualTeamConfirm = false;
            this.dualTeamError = '';
        },
        dualTeamActionLabel(baseLabel) {
            if (this.dualTeamWorking) return 'Working…';
            if (this.dualTeamPreviewLoading) return 'Checking…';
            if (this.awaitingDualTeamConfirm) {
                const amount =
                    this.dualTeamPreview &&
                    !this.dualTeamPreview.deferred &&
                    !this.dualTeamPreview.same_price &&
                    this.dualTeamPreview.amount_due_display;
                return amount ? `Confirm — charge $${amount}` : 'Confirm switch';
            }
            return baseLabel;
        },
        async confirmOrPreviewDualTeamUpgrade() {
            if (!this.dualTeamTarget || this.dualTeamWorking) {
                return;
            }
            if (this.awaitingDualTeamConfirm) {
                await this.applyDualTeamUpgrade();
                return;
            }
            this.dualTeamPreviewLoading = true;
            this.dualTeamError = '';
            try {
                this.dualTeamPreview = await this.$store.dispatch('previewNotesListSwitch', {
                    target: this.dualTeamTarget,
                    interval: 'year'
                });
                this.awaitingDualTeamConfirm = true;
            } catch (err) {
                this.dualTeamPreview = null;
                this.dualTeamError = (err && err.message) || 'Could not preview switch.';
            } finally {
                this.dualTeamPreviewLoading = false;
            }
        },
        async applyDualTeamUpgrade() {
            this.dualTeamWorking = true;
            this.dualTeamError = '';
            try {
                const result = await this.$store.dispatch('applyNotesListPlanSwitch', {
                    target: this.dualTeamTarget
                });
                if (result && result.mode === 'requires_action') {
                    this.dualTeamError =
                        'Your card needs extra verification for this charge. Please try again from Account Settings.';
                    return;
                }
                const label =
                    this.dualTeamTarget === 'dual_educational'
                        ? 'Cuelist + Notes List Educational'
                        : 'Cuelist + Notes List Institutional';
                this.$toast.success(`Upgraded to ${label}.`);
                this.close();
                this.$emit('plans-updated');
            } catch (err) {
                this.dualTeamError = (err && err.message) || 'Upgrade failed. Please try again.';
            } finally {
                this.dualTeamWorking = false;
            }
        }
    }
};
</script>

<style lang="scss">
.noteslist-plans-modal {
    .md-dialog {
        min-width: 600px;
        max-width: 1024px;
        width: 900px;
    }
}

.noteslist-plans-modal-title {
    font-size: 20px;
    font-weight: 600;
    color: #0d2744;
}

.noteslist-plans-modal-content {
    max-height: 70vh;
    overflow-y: auto;
    padding-top: 8px;
}

.noteslist-plans-status-summary {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    margin-bottom: 8px;
}

.noteslist-plans-status-text {
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 0;
}

.noteslist-plans-summary-label {
    font-size: 14px;
    font-weight: 600;
    color: #0d2744;
}

.noteslist-plans-active-plan-name {
    font-size: 15px;
    font-weight: 500;
    line-height: 1.4;
    color: #0d2744;
}

.noteslist-plans-summary-hint {
    margin: 0;
    font-size: 13px;
    color: rgba(13, 39, 68, 0.72);
}

.noteslist-plans-status-block {
    margin-bottom: 20px;
}

.noteslist-plans-section {
    border-top: 1px solid rgba(13, 39, 68, 0.12);
    padding: 20px 0;
}

.noteslist-plans-section-title {
    margin: 0 0 8px;
    font-size: 16px;
    font-weight: 600;
    color: #0d2744;
}

.noteslist-plans-section-copy {
    margin: 0 0 20px;
    font-size: 13px;
    line-height: 1.5;
    color: rgba(13, 39, 68, 0.72);

    &--flush {
        margin-bottom: 0;
    }
}

.noteslist-plans-actions-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    flex-wrap: wrap;
}

.noteslist-plans-interval-row {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-bottom: 0;
}

.noteslist-plans-chip {
    border: 1px solid rgba(13, 39, 68, 0.2);
    background: #fff;
    color: #0d2744;
    border-radius: 4px;
    padding: 8px 12px;
    font-size: 13px;
    cursor: pointer;

    &.active {
        border-color: #5ecfdf;
        background: rgba(94, 207, 223, 0.15);
        font-weight: 600;
    }

    &:disabled {
        opacity: 0.45;
        cursor: not-allowed;
        background: #fff;
    }
}

.noteslist-plans-action-btn {
    margin: 0 0 0 auto !important;
}

.noteslist-plans-error {
    margin: 8px 0 0;
    font-size: 13px;
    color: #c62828;
}

.noteslist-plans-preview {
    margin: 8px 0 0;
    font-size: 13px;
    color: #0d2744;
}

.noteslist-plans-cancel-link {
    font-size: 13px;
    color: rgba(13, 39, 68, 0.72);
}

.noteslist-plans-modal .noteslist-account-badge {
    display: inline-block;
    flex-shrink: 0;
    padding: 4px 10px;
    margin-top: 2px;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    border-radius: 999px;
    white-space: nowrap;

    &.is-linked {
        color: #1b5e20;
        background: #e8f5e9;
    }

    &.is-disconnected {
        color: #6d4c00;
        background: #fff8e1;
    }
}
</style>


// WEBPACK FOOTER //
// noteslist-plans.vue?51df6df9